"use client"

import { useState, useEffect } from "react"
import { Connection, PublicKey, clusterApiUrl } from "@solana/web3.js"
import { WalletAdapterNetwork } from "@solana/wallet-adapter-base"
import { WalletProvider, useWallet } from "@solana/wallet-adapter-react"
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui"
import { Navigation } from "./components/navigation"
import { PhantomWalletAdapter, SolflareWalletAdapter } from "@solana/wallet-adapter-wallets"
import { SubscriptionForm } from "./components/subscription-form"
import { WithdrawalForm } from "./components/withdrawal-form"
import { SwapForm } from "./components/swap-form"
import { WalletBalance } from "./components/wallet-balance"
import { SubscriptionPlans } from "./components/subscription-plans"
import { StakingForm } from "@/components/staking-form"
import { StakingDashboard } from "@/components/staking-dashboard"
import { AdminPanel } from "./components/admin-panel"
import { Footer } from "./components/footer"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { apiClient } from "@/lib/api-client"
import { IRMASubscriptionClient, IRMA_PROGRAM_ID } from "@/lib/irma-client-browser"
import type { AffiliateAccount, Subscription } from "@/lib/api-client"
import "@solana/wallet-adapter-react-ui/styles.css"

// Default mint address - should be set via environment variable
const DEFAULT_MINT = process.env.NEXT_PUBLIC_IRMA_MINT || "11111111111111111111111111111111"

function MainContent() {
  const { publicKey, signTransaction, signAllTransactions } = useWallet()
  const [network] = useState(WalletAdapterNetwork.Devnet)
  const [mint, setMint] = useState(DEFAULT_MINT)
  const [affiliateAccount, setAffiliateAccount] = useState<AffiliateAccount | null>(null)
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [walletBalanceKey, setWalletBalanceKey] = useState(0) // Key to force WalletBalance refresh

  const connection = new Connection(
    network === WalletAdapterNetwork.Devnet
      ? clusterApiUrl("devnet")
      : clusterApiUrl("mainnet-beta"),
    "confirmed"
  )

  // Load affiliate account
  useEffect(() => {
    if (publicKey) {
      loadAffiliateAccount()
      loadSubscriptions()
    }
  }, [publicKey])

  const loadAffiliateAccount = async () => {
    if (!publicKey) return
    try {
      // Try to get from API first
      let account = await apiClient.getAffiliateAccount(publicKey.toString())
      
      // Also fetch from on-chain to get the latest totalWithdrawn
      try {
        const wallet = {
          publicKey,
          signTransaction,
          signAllTransactions,
        }
        const client = new IRMASubscriptionClient(connection, wallet)
        const onChainAccount = await client.getAffiliateAccount(publicKey)
        
        // Update from on-chain data (more accurate)
        // On-chain account has: total_credits, total_withdrawn, available_balance
        // Anchor converts snake_case to camelCase, so check both
        if (onChainAccount) {
          // Try both camelCase and snake_case
          const totalWithdrawn = onChainAccount.totalWithdrawn || onChainAccount.total_withdrawn || onChainAccount["total_withdrawn"]
          const totalCredits = onChainAccount.totalCredits || onChainAccount.total_credits || onChainAccount["total_credits"]
          const availableBalance = onChainAccount.availableBalance || onChainAccount.available_balance || onChainAccount["available_balance"]
          
          // Helper to convert BN to string
          const bnToString = (bn: any): string => {
            if (!bn) return "0"
            if (typeof bn.toString === 'function') return bn.toString()
            if (typeof bn.toNumber === 'function') return bn.toNumber().toString()
            return String(bn)
          }
          
          const totalWithdrawnStr = bnToString(totalWithdrawn) || account.totalWithdrawn || "0"
          const totalCreditsStr = bnToString(totalCredits) || account.totalCredits || "0"
          let availableBalanceStr = bnToString(availableBalance) || account.availableBalance || "0"
          
          // Migration logic: If available_balance is 0 but total_credits > 0, 
          // this is likely an old account that needs migration
          // Set available_balance = total_credits - total_withdrawn as fallback
          const totalCreditsNum = parseFloat(totalCreditsStr)
          const totalWithdrawnNum = parseFloat(totalWithdrawnStr)
          const availableBalanceNum = parseFloat(availableBalanceStr)
          
          if (availableBalanceNum === 0 && totalCreditsNum > 0) {
            // Old account - migrate by setting available_balance = total_credits - total_withdrawn
            const migratedBalance = totalCreditsNum - totalWithdrawnNum
            availableBalanceStr = Math.max(0, migratedBalance).toString()
            console.log("🔄 Migrated old affiliate account:", {
              totalCredits: totalCreditsStr,
              totalWithdrawn: totalWithdrawnStr,
              migratedAvailableBalance: availableBalanceStr,
            })
          }
          
          account = {
            ...account,
            totalWithdrawn: totalWithdrawnStr,
            totalCredits: totalCreditsStr,
            availableBalance: availableBalanceStr,
          }
          console.log("✅ Updated affiliate account from on-chain:", {
            totalWithdrawn: account.totalWithdrawn,
            totalCredits: account.totalCredits,
            availableBalance: account.availableBalance,
            rawOnChain: {
              totalWithdrawn: bnToString(totalWithdrawn),
              totalCredits: bnToString(totalCredits),
              availableBalance: bnToString(availableBalance),
            },
          })
        }
      } catch (onChainError: any) {
        // If on-chain fetch fails, just use API data
        console.warn("Could not fetch affiliate account from on-chain:", onChainError.message)
      }
      
      setAffiliateAccount(account)
    } catch (err: any) {
      if (!err.message.includes("not found")) {
        console.error("Error loading affiliate account:", err)
      }
    }
  }

  // Helper function to convert hex string or decimal string to number
  // Detects if value is hex (short, contains a-f) or decimal (long, all digits)
  const hexToNumber = (value: string | number | undefined): number => {
    if (!value && value !== 0) return 0
    if (typeof value === 'number') return value
    
    const str = value.toString().trim()
    if (!str || str === '0') return 0
    
    // Remove '0x' prefix if present
    const cleanStr = str.startsWith('0x') ? str.slice(2) : str
    
    // Check if string contains hex characters (a-f, A-F)
    const hasHexChars = /[a-fA-F]/.test(cleanStr)
    
    // If string is long (> 10 chars) and all digits, it's likely decimal
    // If string contains hex chars, it's hex
    // If string is short and all digits, try hex first (common for small hex values)
    if (cleanStr.length > 10 && !hasHexChars) {
      // Long decimal number - parse as decimal
      const decimal = parseFloat(cleanStr)
      return isNaN(decimal) ? 0 : Math.floor(decimal)
    } else if (hasHexChars || cleanStr.length <= 8) {
      // Likely hex - parse as hex
      const hexValue = parseInt(cleanStr, 16)
      if (!isNaN(hexValue)) {
        return hexValue
      }
    }
    
    // Fallback: try decimal parse
    const decimal = parseFloat(cleanStr)
    return isNaN(decimal) ? 0 : Math.floor(decimal)
  }

  const loadSubscriptions = async () => {
    if (!publicKey) return
    try {
      const subs = await apiClient.getAllSubscriptions(publicKey.toString())
      // Process subscriptions to convert hex values to proper numbers
      const processed = subs.map((s: any) => {
        const planPrice = hexToNumber(s.account.planPrice)
        const startDate = hexToNumber(s.account.startDate)
        const expiryDate = hexToNumber(s.account.expiryDate)
        const affiliateCredit = hexToNumber(s.account.affiliateCredit)
        
        console.log('Processing subscription:', {
          planId: s.account.planId,
          planPriceHex: s.account.planPrice,
          planPriceDecimal: planPrice,
          planPriceIRMA: planPrice / 1_000_000_000,
          expiryDateHex: s.account.expiryDate,
          expiryDateDecimal: expiryDate,
          expiryDateFormatted: new Date(expiryDate * 1000).toLocaleString()
        })
        
        return {
          ...s.account,
          planPrice,
          startDate,
          expiryDate,
          affiliateCredit,
          auto_renew_enabled: s.account.autoRenewEnabled ?? s.account.auto_renew_enabled ?? false,
          autoRenewEnabled: s.account.autoRenewEnabled ?? s.account.auto_renew_enabled ?? false,
        }
      })
      setSubscriptions(processed)
    } catch (err) {
      console.error("Error loading subscriptions:", err)
    }
  }

  const handleSubscribe = async (platformId: string, irmaAmount: number, durationDays: number, autoRenew: boolean = false) => {
    if (!publicKey || !signTransaction) {
      setError("Please connect your wallet")
      return
    }

    setLoading(true)
    setError("")

    try {
      const wallet = {
        publicKey,
        signTransaction,
        signAllTransactions,
      }

      const client = new IRMASubscriptionClient(connection, wallet)
      const mintPubkey = new PublicKey(mint)

      // Check if subscription vault is initialized, if not, initialize it first
      const isVaultInitialized = await client.isSubscriptionVaultInitialized()
      if (!isVaultInitialized) {
        console.log("Subscription vault not initialized, initializing...")
        try {
          const initTx = await client.initializeSubscriptionVault(publicKey)
          console.log("Vault initialization transaction:", initTx)
          // Wait for the transaction to confirm
          await connection.confirmTransaction(initTx, "confirmed")
          console.log("Vault initialized successfully")
        } catch (initError: any) {
          // If initialization fails, check if it was already initialized by another user
          const checkAgain = await client.isSubscriptionVaultInitialized()
          if (!checkAgain) {
            throw new Error(`Failed to initialize vault: ${initError.message}`)
          }
          console.log("Vault was already initialized by another user")
        }
      }

      // Subscribe using the client with auto-renewal flag
      const tx = await client.subscribe(publicKey, mintPubkey, irmaAmount, durationDays, autoRenew)
      console.log("Subscription transaction:", tx)

      // Wait a bit for the transaction to be confirmed on-chain
      await connection.confirmTransaction(tx, "confirmed")
      console.log("Subscription transaction confirmed")

      // Save subscription details to database with autoRenewEnabled
      try {
        await apiClient.saveSubscription(publicKey.toString(), platformId, tx, autoRenew)
        console.log("✅ Subscription saved to database with autoRenewEnabled:", autoRenew)
      } catch (dbError: any) {
        console.warn("⚠️ Failed to save subscription to database:", dbError.message)
        // Don't fail the whole operation if DB save fails - on-chain is the source of truth
        // Try to sync from on-chain as fallback
        try {
          await apiClient.syncSubscription(publicKey.toString(), platformId)
          console.log("✅ Subscription synced from on-chain as fallback")
        } catch (syncError: any) {
          console.warn("⚠️ Failed to sync subscription:", syncError.message)
        }
      }

      // Reload data after successful subscription
      await loadSubscriptions()
      await loadAffiliateAccount()

      const message = autoRenew 
        ? `Subscription successful with auto-renewal enabled! Transaction: ${tx}`
        : `Subscription successful! Transaction: ${tx}`
      alert(message)
    } catch (err: any) {
      setError(err.message || "Subscription failed")
      console.error("Subscribe error:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleEnableAutoRenew = async (platformId: string) => {
    if (!publicKey || !signTransaction) {
      setError("Please connect your wallet")
      return
    }

    setLoading(true)
    setError("")

    try {
      const wallet = {
        publicKey,
        signTransaction: async (tx: any) => {
          if (!signTransaction) {
            throw new Error("Wallet signTransaction not available")
          }
          return await signTransaction(tx)
        },
        signAllTransactions: async (txs: any[]) => {
          if (!signAllTransactions) {
            throw new Error("Wallet signAllTransactions not available")
          }
          return await signAllTransactions(txs)
        },
      }

      const client = new IRMASubscriptionClient(connection, wallet)
      const mintPubkey = new PublicKey(mint)

      // First, approve delegation if not already approved
      try {
        const { approveAutoRenewalDelegation, checkDelegationApproved, getAutoRenewDelegatePDA } = await import("@/lib/auto-renewal")
        const [delegatePDA] = getAutoRenewDelegatePDA(publicKey)
        const delegation = await checkDelegationApproved(connection, publicKey, mintPubkey, delegatePDA)
        
        // Get subscription to know the price
        const subscription = await client.getSubscription(publicKey)
        const planPrice = subscription?.planPrice?.toNumber() || 0

        if (!delegation.approved || delegation.amount < planPrice) {
          console.log("Approving auto-renewal delegation...")
          await approveAutoRenewalDelegation(
            connection,
            publicKey,
            mintPubkey,
            planPrice,
            {
              publicKey,
              signTransaction: wallet.signTransaction,
            }
          )
        }
      } catch (delegationError: any) {
        console.warn("Delegation check/approval failed:", delegationError)
        // Continue anyway - user might have already approved
      }

      // Enable auto-renewal
      const tx = await client.enableAutoRenew(publicKey)
      console.log("Enable auto-renewal transaction:", tx)
      await connection.confirmTransaction(tx, "confirmed")

      // Update database with auto-renewal enabled
      try {
        await apiClient.enableAutoRenew(publicKey.toString(), platformId)
        console.log("✅ Auto-renewal enabled in database")
      } catch (dbError: any) {
        console.warn("⚠️  Failed to update database:", dbError.message)
        // Continue anyway - on-chain is the source of truth
      }

      // Reload subscriptions
      await loadSubscriptions()

      alert(`Auto-renewal enabled! Transaction: ${tx}`)
    } catch (err: any) {
      setError(err.message || "Failed to enable auto-renewal")
      console.error("Enable auto-renewal error:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleDisableAutoRenew = async (platformId: string) => {
    if (!publicKey || !signTransaction) {
      setError("Please connect your wallet")
      return
    }

    setLoading(true)
    setError("")

    try {
      const wallet = {
        publicKey,
        signTransaction,
        signAllTransactions,
      }

      const client = new IRMASubscriptionClient(connection, wallet)

      // Disable auto-renewal
      const tx = await client.disableAutoRenew(publicKey)
      console.log("Disable auto-renewal transaction:", tx)
      await connection.confirmTransaction(tx, "confirmed")

      // Update database with auto-renewal disabled
      try {
        await apiClient.disableAutoRenew(publicKey.toString(), platformId)
        console.log("✅ Auto-renewal disabled in database")
      } catch (dbError: any) {
        console.warn("⚠️  Failed to update database:", dbError.message)
        // Continue anyway - on-chain is the source of truth
      }

      // Reload subscriptions
      await loadSubscriptions()

      alert(`Auto-renewal disabled! Transaction: ${tx}`)
    } catch (err: any) {
      setError(err.message || "Failed to disable auto-renewal")
      console.error("Disable auto-renewal error:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleCancelSubscription = async (platformId: string) => {
    if (!publicKey || !signTransaction) {
      setError("Please connect your wallet")
      return
    }

    // Confirm cancellation
    const confirmed = window.confirm(
      "Are you sure you want to cancel this subscription? This action cannot be undone."
    )
    if (!confirmed) {
      return
    }

    setLoading(true)
    setError("")

    try {
      const wallet = {
        publicKey,
        signTransaction,
        signAllTransactions,
      }

      const client = new IRMASubscriptionClient(connection, wallet)

      // Cancel subscription
      const tx = await client.cancelSubscription(publicKey)
      console.log("Cancel subscription transaction:", tx)
      await connection.confirmTransaction(tx, "confirmed")

      // Reload subscriptions
      await loadSubscriptions()

      alert(`Subscription cancelled! Transaction: ${tx}`)
    } catch (err: any) {
      setError(err.message || "Failed to cancel subscription")
      console.error("Cancel subscription error:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleWithdraw = async (amount: number) => {
    if (!publicKey || !signTransaction) {
      setError("Please connect your wallet")
      return
    }

    setLoading(true)
    setError("")

    try {
      const wallet = {
        publicKey,
        signTransaction,
        signAllTransactions,
      }

      const client = new IRMASubscriptionClient(connection, wallet)
      const mintPubkey = new PublicKey(mint)
      const amountInSmallestUnit = Math.floor(amount * 1_000_000_000) // Convert to smallest unit (9 decimals for IRMA)
      const vault = client.getWithdrawalVault()

      const tx = await client.withdrawRewards(publicKey, amountInSmallestUnit, mintPubkey)
      console.log("Withdrawal transaction:", tx)

      // Wait for the transaction to be confirmed on-chain
      console.log("Waiting for transaction confirmation...")
      const confirmation = await connection.confirmTransaction(tx, "confirmed")
      console.log("Transaction confirmation:", confirmation)
      
      if (confirmation.value.err) {
        throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`)
      }
      
      // Get transaction details to verify it succeeded
      try {
        const txDetails = await connection.getTransaction(tx, {
          commitment: "confirmed",
          maxSupportedTransactionVersion: 0,
        })
        console.log("📋 Transaction details:", {
          err: txDetails?.meta?.err,
          fee: txDetails?.meta?.fee,
          logMessages: txDetails?.meta?.logMessages?.slice(-10), // Last 10 log messages
        })
        
        if (txDetails?.meta?.err) {
          throw new Error(`Transaction error: ${JSON.stringify(txDetails.meta.err)}`)
        }
        
        // Check if withdrawal was logged
        const hasWithdrawalLog = txDetails?.meta?.logMessages?.some((log: string) => 
          log.includes("WithdrawalCompleted") || log.includes("withdraw_rewards")
        )
        console.log("Withdrawal log found:", hasWithdrawalLog)
        
        if (!hasWithdrawalLog) {
          console.warn("⚠️  Warning: No withdrawal completion log found in transaction")
        }
      } catch (txError) {
        console.warn("Could not fetch transaction details:", txError)
      }
      
      console.log("✅ Withdrawal transaction confirmed successfully")

      // Save withdrawal record to backend
      try {
        await apiClient.saveWithdrawal(
          publicKey.toString(),
          amountInSmallestUnit.toString(),
          tx,
          vault.toString(),
          "completed"
        )
        console.log("✅ Withdrawal record saved to backend")
      } catch (apiError: any) {
        console.warn("⚠️  Failed to save withdrawal to backend:", apiError.message)
        // Don't fail the withdrawal if backend save fails - transaction already succeeded
      }

      // Wait a bit more to ensure on-chain state is updated
      console.log("Waiting for on-chain state to update...")
      await new Promise(resolve => setTimeout(resolve, 3000))

      // Reload affiliate account (this will also update totalWithdrawn from on-chain)
      console.log("Reloading affiliate account...")
      await loadAffiliateAccount()
      
      // Force wallet balance refresh
      setWalletBalanceKey(prev => prev + 1)
      
      // Double-check the account was updated by reading directly from on-chain
      try {
        const wallet = {
          publicKey,
          signTransaction,
          signAllTransactions,
        }
        const checkClient = new IRMASubscriptionClient(connection, wallet)
        const updatedAccount = await checkClient.getAffiliateAccount(publicKey)
        console.log("✅ On-chain account after withdrawal:", {
          totalWithdrawn: updatedAccount.totalWithdrawn?.toString() || "0",
          totalCredits: updatedAccount.totalCredits?.toString() || "0",
          availableBalance: updatedAccount.availableBalance?.toString() || "0",
        })
        
        // Verify the withdrawal actually happened
        const expectedWithdrawn = amountInSmallestUnit
        const actualWithdrawn = updatedAccount.totalWithdrawn?.toNumber() || updatedAccount.total_withdrawn?.toNumber() || 0
        const beforeCredits = 1001000000000 // From earlier logs
        const afterCredits = updatedAccount.totalCredits?.toNumber() || updatedAccount.total_credits?.toNumber() || 0
        const creditsDeducted = beforeCredits - afterCredits
        
        console.log("🔍 Withdrawal Verification:", {
          expectedWithdrawn,
          actualWithdrawn,
          creditsDeducted,
          creditsMatch: creditsDeducted === expectedWithdrawn,
          withdrawnUpdated: actualWithdrawn > 0,
        })
        
        if (actualWithdrawn === 0 && creditsDeducted > 0) {
          console.error("❌ CRITICAL: Credits were deducted but totalWithdrawn was NOT updated!")
          console.error("This suggests the transaction partially failed or the contract has a bug.")
          console.error("Please check the transaction logs for errors.")
        } else if (actualWithdrawn > 0) {
          console.log(`✅ Verified: totalWithdrawn updated to ${actualWithdrawn}`)
        }
      } catch (checkError) {
        console.error("Error checking on-chain account:", checkError)
      }

      alert(`Withdrawal successful! Transaction: ${tx.slice(0, 8)}...`)
    } catch (err: any) {
      setError(err.message || "Withdrawal failed")
      console.error("Withdraw error:", err)
    } finally {
      setLoading(false)
    }
  }

  // IRMA token has 9 decimals, not 6
  const availableBalance = affiliateAccount
    ? parseFloat(affiliateAccount.availableBalance) / 1_000_000_000
    : 0

  return (
    <div className="min-h-screen bg-[#0A0D14] relative"
      style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)', backgroundSize: '24px 24px', backgroundAttachment: 'fixed' }}>
      {/* Gradient orbs - fixed so they stay in place while scrolling */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ background: '#25e4e8' }}></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ background: 'purple' }}></div>
      </div>

      <Navigation />

      <div className="max-w-6xl mx-auto space-y-8 relative z-10 p-8 pt-16 pb-16">
        <div className="flex flex-col items-center text-center animate-slide-up">
          <div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">
              IRMA Subscriptions
            </h1>
            <p className="text-[#8B949E] mt-3 text-base sm:text-lg">
              Decentralized subscription platform on Solana
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        <Tabs defaultValue="plans" className="w-full">
          <TabsList className="grid w-full grid-cols-8 bg-[#13161C] p-1 rounded-xl border border-white/5 mb-6">
            <TabsTrigger value="plans" className="rounded-lg text-[#8B949E] transition-all data-[state=active]:bg-[#25e4e8] data-[state=active]:text-black data-[state=active]:font-bold">Plans</TabsTrigger>
            <TabsTrigger value="swap" className="rounded-lg text-[#8B949E] transition-all data-[state=active]:bg-[#25e4e8] data-[state=active]:text-black data-[state=active]:font-bold">Swap</TabsTrigger>
            <TabsTrigger value="subscribe" className="rounded-lg text-[#8B949E] transition-all data-[state=active]:bg-[#25e4e8] data-[state=active]:text-black data-[state=active]:font-bold">Subscribe</TabsTrigger>
            <TabsTrigger value="staking" className="rounded-lg text-[#8B949E] transition-all data-[state=active]:bg-[#25e4e8] data-[state=active]:text-black data-[state=active]:font-bold">Staking</TabsTrigger>
            <TabsTrigger value="withdraw" className="rounded-lg text-[#8B949E] transition-all data-[state=active]:bg-[#25e4e8] data-[state=active]:text-black data-[state=active]:font-bold">Withdraw</TabsTrigger>
            <TabsTrigger value="subscriptions" className="rounded-lg text-[#8B949E] transition-all data-[state=active]:bg-[#25e4e8] data-[state=active]:text-black data-[state=active]:font-bold">My Subscriptions</TabsTrigger>
            <TabsTrigger value="affiliate" className="rounded-lg text-[#8B949E] transition-all data-[state=active]:bg-[#25e4e8] data-[state=active]:text-black data-[state=active]:font-bold">Affiliate</TabsTrigger>
            <TabsTrigger value="admin" className="rounded-lg text-[#8B949E] transition-all data-[state=active]:bg-[#25e4e8] data-[state=active]:text-black data-[state=active]:font-bold">Admin</TabsTrigger>
          </TabsList>

          <TabsContent value="plans" className="space-y-4 animate-slide-up">
            <SubscriptionPlans />
          </TabsContent>

          <TabsContent value="swap" className="space-y-4 animate-slide-up">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SwapForm connection={connection} onSwapComplete={loadAffiliateAccount} />
              <WalletBalance key={walletBalanceKey} connection={connection} irmaMint={mint} />
            </div>
          </TabsContent>

          <TabsContent value="subscribe" className="space-y-4 animate-slide-up">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SubscriptionForm 
                onSubscribe={handleSubscribe} 
                connection={connection}
                irmaMint={mint}
              />
              <WalletBalance key={walletBalanceKey} connection={connection} irmaMint={mint} />
            </div>
          </TabsContent>

          <TabsContent value="staking" className="space-y-4 animate-slide-up">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <StakingForm 
                connection={connection}
                irmaMint={mint}
                onStakeComplete={loadAffiliateAccount}
              />
              <StakingDashboard 
                connection={connection}
                irmaMint={mint}
              />
            </div>
          </TabsContent>

          <TabsContent value="withdraw" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <WithdrawalForm
                onWithdraw={handleWithdraw}
                availableBalance={availableBalance}
              />
              {affiliateAccount && (
                <div className="bg-[#13161C] rounded-3xl p-3 border border-white/5 shadow-2xl">
                  <div className="px-1 mb-3">
                    <div className="text-lg font-bold text-white">Affiliate Account</div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
                      <p className="text-sm text-[#8B949E]">Total Credits</p>
                      <p className="text-xl font-bold text-white">{parseFloat(affiliateAccount.totalCredits) / 1_000_000_000} IRMA</p>
                    </div>
                    <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
                      <p className="text-sm text-[#8B949E]">Total Withdrawn</p>
                      <p className="text-xl font-bold text-white">{parseFloat(affiliateAccount.totalWithdrawn) / 1_000_000_000} IRMA</p>
                    </div>
                    <div className="bg-[#1C1F26] rounded-2xl p-4 border border-[#25e4e8]/20">
                      <p className="text-sm text-[#8B949E]">Available</p>
                      <p className="text-xl font-bold text-[#25e4e8]">{availableBalance.toFixed(2)} IRMA</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="subscriptions" className="space-y-4">
            <div className="bg-[#13161C] rounded-3xl p-3 border border-white/5 shadow-2xl">
              <div className="px-1 mb-3">
                <div className="text-lg font-bold text-white">My Subscriptions</div>
              </div>
              {subscriptions.length === 0 ? (
                <div className="bg-[#1C1F26] rounded-2xl p-12 text-center">
                  <p className="text-[#8B949E] text-lg">No subscriptions found</p>
                  <p className="text-sm text-[#8B949E] mt-2">Subscribe to a plan to get started!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {subscriptions.map((sub: any, idx: number) => {
                    const price = (typeof sub.planPrice === 'number' ? sub.planPrice : parseInt(sub.planPrice, 16)) / 1_000_000_000
                    const expiryTimestamp = typeof sub.expiryDate === 'number' ? sub.expiryDate : parseInt(sub.expiryDate, 16)
                    const expiryDate = new Date(expiryTimestamp * 1000)
                    const isExpired = expiryDate < new Date()
                    const autoRenewEnabled = sub.auto_renew_enabled || sub.autoRenewEnabled || false
                    
                    return (
                      <div key={idx} className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-lg text-white">{sub.planId}</span>
                            <div className="flex items-center gap-2">
                              {autoRenewEnabled && (
                                <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-400 border border-blue-500/30">Auto-Renew</span>
                              )}
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                sub.isActive && !isExpired ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-gray-500/20 text-[#8B949E] border border-gray-500/30'
                              }`}>
                                {sub.isActive && !isExpired ? "Active" : "Expired"}
                              </span>
                            </div>
                          </div>
                          <div className="bg-[#13161C] rounded-xl p-3 border border-[#25e4e8]/20">
                            <p className="text-[#8B949E] text-xs mb-1">Price Paid</p>
                            <p className="font-bold text-xl text-[#25e4e8]">{price.toFixed(4)} IRMA</p>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-[#13161C] rounded-xl p-2">
                              <p className="text-[#8B949E] text-xs mb-1">Started</p>
                              <p className="font-semibold text-xs text-white">
                                {(() => {
                                  const startTimestamp = typeof sub.startDate === 'number' ? sub.startDate : parseInt(sub.startDate, 16)
                                  return new Date(startTimestamp * 1000).toLocaleDateString()
                                })()}
                              </p>
                            </div>
                            <div className="bg-[#13161C] rounded-xl p-2">
                              <p className="text-[#8B949E] text-xs mb-1">Expires</p>
                              <p className="font-semibold text-xs text-white">{expiryDate.toLocaleDateString()}</p>
                            </div>
                          </div>
                          {sub.isActive && !isExpired && (
                            <div className="pt-2 border-t border-white/5 space-y-2">
                              {autoRenewEnabled ? (
                                <button onClick={() => handleDisableAutoRenew(sub.planId)} disabled={loading}
                                  className="w-full bg-[#1C1F26] hover:bg-[#252830] border border-white/5 text-white text-sm font-semibold py-2 rounded-xl transition disabled:opacity-50">
                                  Disable Auto-Renewal
                                </button>
                              ) : (
                                <button onClick={() => handleEnableAutoRenew(sub.planId)} disabled={loading}
                                  className="w-full bg-[#25e4e8] hover:bg-[#1dd1d5] text-black text-sm font-bold py-2 rounded-xl transition disabled:opacity-50">
                                  Enable Auto-Renewal
                                </button>
                              )}
                              <button onClick={() => handleCancelSubscription(sub.planId)} disabled={loading}
                                className="w-full bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400 text-sm font-semibold py-2 rounded-xl transition disabled:opacity-50">
                                Cancel Subscription
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="affiliate" className="space-y-4">
            <div className="bg-[#13161C] rounded-3xl p-3 border border-white/5 shadow-2xl">
              <div className="px-1 mb-3">
                <div className="text-lg font-bold text-white">Affiliate Dashboard</div>
              </div>
              {affiliateAccount ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
                      <p className="text-sm text-[#8B949E] mb-2">Total Credits</p>
                      <p className="text-3xl font-bold text-white">
                        {(parseFloat(affiliateAccount.totalCredits) / 1_000_000_000).toFixed(2)} IRMA
                      </p>
                    </div>
                    <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
                      <p className="text-sm text-[#8B949E] mb-2">Total Withdrawn</p>
                      <p className="text-3xl font-bold text-white">
                        {(parseFloat(affiliateAccount.totalWithdrawn) / 1_000_000_000).toFixed(2)} IRMA
                      </p>
                    </div>
                    <div className="bg-[#1C1F26] rounded-2xl p-4 border border-[#25e4e8]/20">
                      <p className="text-sm text-[#8B949E] mb-2">Available</p>
                      <p className="text-3xl font-bold text-[#25e4e8]">{availableBalance.toFixed(2)} IRMA</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-[#1C1F26] rounded-2xl p-12 text-center">
                  <p className="text-[#8B949E] text-lg">
                    {publicKey
                      ? "No affiliate account found. Earn credits by referring subscriptions!"
                      : "Connect your wallet to view affiliate information"}
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="admin" className="space-y-4">
            <AdminPanel connection={connection} irmaMint={mint} />
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  )
}

export default function Home() {
  const [network] = useState(WalletAdapterNetwork.Devnet)
  const [wallets] = useState(() => [
    new PhantomWalletAdapter(),
    new SolflareWalletAdapter(),
  ])

  return (
    <WalletProvider wallets={wallets} autoConnect>
      <WalletModalProvider>
        <MainContent />
      </WalletModalProvider>
    </WalletProvider>
  )
}